import { useState, useMemo, useRef, useEffect } from "react";
import { Archive, Check, ChevronDown, ChevronsUpDown } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Fuse from "fuse.js";

import { cn } from "src/@/lib/utils";
import { Button } from "src/@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "src/@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "src/@/components/ui/popover";
import { useMyAdminStores } from "#store-selector/hooks";
import { setActiveStoreIdSliceId } from "src/redux/slices";
import { getActiveStore, getActiveStoreLogoUrl } from "src/redux/selectors";
import {
    Drawer,
    DrawerContent,
    DrawerTrigger,
} from "src/@/components/ui/drawer";
import { useMediaQuery } from "src/@/hooks/use-media-query";

const MAX_RESULTS = 20;

export function StoreCombobox() {
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const isDesktop = useMediaQuery("(min-width: 768px)");

    const { stores, isPending } = useMyAdminStores();
    const hasMultipleStores = stores.length > 1;
    const dispatch = useDispatch();
    const activeStore = useSelector(getActiveStore);
    const activeStoreLogoUrl = useSelector(getActiveStoreLogoUrl);
    const nonArchivedStores = useMemo(
        () => stores.filter((store) => !store.isArchived),
        [stores],
    );

    const fuse = useMemo(() => {
        return new Fuse(nonArchivedStores, {
            keys: [
                { name: "name", weight: 2 }, // Give name matches higher priority
                { name: "address", weight: 1 },
            ],
            isCaseSensitive: false,
            threshold: 0.3, // Slightly more lenient threshold
            distance: 100, // Allow matches to be further apart
            shouldSort: true,
            ignoreLocation: true,
            minMatchCharLength: 2, // Require at least 2 characters to match
        });
    }, [nonArchivedStores]);

    const filteredStores = useMemo(() => {
        if (!searchQuery) {
            return nonArchivedStores.slice(0, MAX_RESULTS);
        }

        // Exact ID match
        const exactMatch = nonArchivedStores.find(store => store._id === searchQuery);
        if (exactMatch) return [exactMatch];

        // Word-based matching introduced to check if query words appear in name OR address. 
        // If no words or matches are found, use fuzzy search as fallback (should not be needed).
        const normalize = (s: string) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const words = normalize(searchQuery).split(/\s+/).filter(w => w.length > 1);

        const matches = words.length === 0 ? [] : nonArchivedStores.filter(store => {
            const text = normalize(`${store.name} ${store.address || ''}`);
            return words.filter(w => text.includes(w)).length >= Math.ceil(words.length * 0.7);
        });

        return (matches.length > 0 ? matches : fuse.search(searchQuery).map(r => r.item))
            .slice(0, MAX_RESULTS);
    }, [nonArchivedStores, searchQuery, fuse]);

    const handleSelect = (currentValue: string) => {
        dispatch(setActiveStoreIdSliceId(currentValue));
        setOpen(false);
        setSearchQuery("");
    };

    if (isPending || !activeStore) {
        return null;
    }

    const StoreHeader = () => (
        <div className="flex items-center w-full">
            {activeStoreLogoUrl ? (
                <div
                    className="aspect-square size-8 rounded-lg bg-sidebar-primary text-sidebar-primary-foreground bg-cover bg-center bg-no-repeat"
                    style={{
                        backgroundImage: `url(${activeStoreLogoUrl})`,
                    }}
                    aria-label={activeStore.name}
                />
            ) : (
                <div className="aspect-square size-8 rounded-lg bg-sidebar-primary text-sidebar-primary-foreground flex items-center justify-center">
                    <span className="text-sm font-medium">
                        {activeStore.name.charAt(0).toUpperCase()}
                    </span>
                </div>
            )}
            <div className="grid flex-1 text-left text-sm leading-tight ml-3">
                <span className="truncate font-semibold">
                    {activeStore.name}
                </span>
                <span className="truncate text-xs">{activeStore.address}</span>
            </div>
            {hasMultipleStores && (
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            )}
        </div>
    );

    if (!hasMultipleStores) {
        return <StoreHeader />;
    }

    if (isDesktop) {
        return (
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="ghost"
                        role="combobox"
                        aria-expanded={open}
                        className="w-full justify-between hover:bg-transparent p-0"
                    >
                        <StoreHeader />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    className="w-[300px] p-0"
                    align="start"
                    sideOffset={8}
                >
                    <StoreSelector
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        filteredStores={filteredStores}
                        activeStore={activeStore}
                        handleSelect={handleSelect}
                    />
                </PopoverContent>
            </Popover>
        );
    }

    return (
        <Drawer open={open} onOpenChange={setOpen}>
            <DrawerTrigger asChild>
                <Button
                    variant="ghost"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between hover:bg-transparent p-0"
                >
                    <StoreHeader />
                </Button>
            </DrawerTrigger>
            <DrawerContent className="h-[50vh]">
                <div className="mt-4 border-t">
                    <StoreSelector
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        filteredStores={filteredStores}
                        activeStore={activeStore}
                        handleSelect={handleSelect}
                    />
                </div>
            </DrawerContent>
        </Drawer>
    );
}

function StoreSelector({
    searchQuery,
    setSearchQuery,
    filteredStores,
    activeStore,
    handleSelect,
}: {
    searchQuery: string;
    setSearchQuery: (value: string) => void;
    filteredStores: any[];
    activeStore: any;
    handleSelect: (value: string) => void;
}) {
    const commandListRef = useRef<HTMLDivElement | null>(null);
    const [showScrollTop, setShowScrollTop] = useState(false);

    useEffect(() => {
        if (commandListRef.current) {
            commandListRef.current.scrollTop = 0;
        }
    }, [searchQuery, filteredStores]);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        setShowScrollTop(e.currentTarget.scrollTop > 100);
    };

    const scrollToTop = () => {
        commandListRef.current?.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    return (
        <Command className="relative" shouldFilter={false}>
            <CommandInput
                placeholder="Search store..."
                value={searchQuery}
                onValueChange={setSearchQuery}
            />
            <CommandList ref={commandListRef} onScroll={handleScroll}>
                <CommandEmpty>No store found.</CommandEmpty>
                <CommandGroup>
                    {filteredStores.map((store) => (
                        <CommandItem
                            key={store._id}
                            value={`${store.name}|${store._id}`}
                            onSelect={() => handleSelect(store._id)}
                            className={cn(
                                "flex items-center justify-between",
                                activeStore._id === store._id && "bg-accent",
                            )}
                        >
                            <div className="flex flex-col flex-1">
                                <div className="flex items-center gap-2">
                                    {store?.isArchived && (
                                        <Archive className="h-3 w-3 text-muted-foreground shrink-0" />
                                    )}
                                    <span className="font-medium">
                                        {store.name}
                                    </span>
                                </div>
                                <span className="text-xs text-muted-foreground">
                                    {store.address}
                                </span>
                            </div>
                            {activeStore._id === store._id && (
                                <Check className="h-4 w-4 shrink-0 opacity-100" />
                            )}
                        </CommandItem>
                    ))}
                </CommandGroup>
            </CommandList>
            {showScrollTop && (
                <Button
                    variant="outline"
                    size="sm"
                    className="absolute bottom-2 right-2 h-8 w-8 rounded-full p-0"
                    onClick={scrollToTop}
                >
                    <ChevronDown className="h-4 w-4 rotate-180" />
                    <span className="sr-only">Scroll to top</span>
                </Button>
            )}
        </Command>
    );
}
